// IDE/ide/helpObject/CharacterHelp.tsx
import React, { useState } from 'react';
import { Button, Col, Modal, Row, Typography, message } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import type { LanguageId } from '../core/types'; // <- 'javascript' | 'python'
import { TSprite } from '../../../../models/objects/sprite';
import { getImagePath } from '../../../../utils/format/valueFormat';
import { SpriteAnimationPreview } from '../../../../pages/admin/sprites/SpriteAnimationPreview';
import { toIdentifier } from '../../../../utils/gen/strings';

const { Title, Paragraph, Text } = Typography;

interface CharacterHelpProps {
    sprites: TSprite[];
    open: boolean;
    onClose: () => void;
    language: LanguageId; // 👈 ajoute le langage courant
}

function makeGlobalSnippet(language: LanguageId) {
    if (language === 'python') {
        return {
            display: `# Pour ajouter un personnage
id1 = addCharacter(name, positionX, positionY)`,
            copy: `# Pour ajouter un personnage
id1 = addCharacter("name", 150, 150)`,
        };
    }
    // javascript
    return {
        display: `// Pour ajouter un personnage
const id1 = addCharacter(name, positionX, positionY);`,
        copy: `// Pour ajouter un personnage
const id1 = addCharacter("name", 150, 150);`,
    };
}

function makePerSpriteSnippet(sprite: TSprite, language: LanguageId) {
    const clean = toIdentifier(sprite.name);
    if (language === 'python') {
        const varName = `id_${clean}`.toLowerCase();
        return `${varName} = addCharacter("${sprite.name}", 150, 150)`;
    }
    // javascript
    return `const id${clean} = addCharacter("${sprite.name}", 150, 150);`;
}

const CharacterHelp: React.FC<CharacterHelpProps> = ({ sprites, open, onClose, language }) => {
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [globalCopied, setGlobalCopied] = useState(false);

    const { display: globalDisplay, copy: globalCopy } = makeGlobalSnippet(language);

    const handleGlobalCopy = () => {
        navigator.clipboard.writeText(globalCopy);
        setGlobalCopied(true);
        message.success('Code copié !');
        setTimeout(() => setGlobalCopied(false), 2000);
    };

    const handleCopySprite = (sprite: TSprite) => {
        const command = makePerSpriteSnippet(sprite, language);
        navigator.clipboard.writeText(command);
        setCopiedId(sprite.id);
        message.success('Code copié !');
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width="90%"
            centered
            styles={{
                body: {
                    maxHeight: 'calc(100vh - 220px)',
                    overflowY: 'auto',
                    paddingRight: 12,
                },
                content: { margin: 0 },
            }}
        >
            <Title level={3}>Personnages</Title>

            {/* Bloc générique */}
            <div
                style={{
                    background: '#fafafa',
                    padding: 16,
                    borderRadius: 8,
                    marginBottom: 24,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
            >
                <Paragraph>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        <code>{globalDisplay}</code>
                    </pre>
                </Paragraph>
                <Button icon={globalCopied ? <CheckOutlined /> : <CopyOutlined />} onClick={handleGlobalCopy}>
                    {globalCopied ? 'Copié' : 'Copier'}
                </Button>
            </div>

            {/* Cartes personnages */}
            <Row gutter={[24, 24]} justify="start">
                {sprites
                    .filter((sprite) => sprite.image_url !== '')
                    .map((sprite) => (
                        <Col key={sprite.id} xs={12} sm={8} md={6} lg={4} xl={3}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                    backgroundColor: '#fff',
                                    padding: 12,
                                    borderRadius: 8,
                                }}
                            >
                                <div
                                    style={{
                                        width: 150,
                                        height: 150,
                                        borderRadius: 8,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        marginBottom: 8,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <SpriteAnimationPreview
                                        imageUrl={getImagePath(sprite.image_url)}
                                        frameWidth={sprite.animations?.[0]?.frame_width || 64}
                                        frameHeight={sprite.animations?.[0]?.frame_height || 64}
                                        frameCount={sprite.animations?.[0]?.frame_count || 1}
                                        frameRate={sprite.animations?.[0]?.frame_rate || 12}
                                        offsetX={sprite.animations?.[0]?.offset_x || 0}
                                        offsetY={sprite.animations?.[0]?.offset_y || 0}
                                        scale={0}
                                    />
                                </div>
                                <Text strong>{sprite.name}</Text>
                                <Button
                                    icon={copiedId === sprite.id ? <CheckOutlined /> : <CopyOutlined />}
                                    onClick={() => handleCopySprite(sprite)}
                                    style={{ marginTop: 8 }}
                                >
                                    {copiedId === sprite.id ? 'Copié' : 'Copier'}
                                </Button>
                            </div>
                        </Col>
                    ))}
            </Row>
        </Modal>
    );
};

export default CharacterHelp;
